import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import type { AddressDto, UserDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { RefreshCcw01Icon } from "@/components/Icons/RefreshCcw01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Upload01Icon } from "@/components/Icons/Upload01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Button } from "@/design-system/Button";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Spread } from "@/design-system/Spread";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { formatAddress } from "@/helpers/address";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { AddressOverviewUserListModal } from "@/modules/addresses/components/AddressOverviewUserListModal";
import { ExportAddressesModal } from "@/modules/addresses/components/ExportAddressesModal";
import { canEditAddresses, canManageAddresses } from "@/modules/addresses/permissions";
import { addressMutations } from "@/queries/addresses/mutations";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function AddressListPage(): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<AddressDto>("address-delete-modal");
  const [locationCodeModalAddressId, setLocationCodeModalAddressId] = useState<string>();
  const [isRefreshingLocationCode, refreshingLocationCodeHandlers] = useBool();
  const [search, setSearch] = useState("");
  const [selectedAddressTypes, setSelectedAddressTypes] = useState<string[]>([]);
  const debouncedQuery = useDebounceValue(search.toLowerCase().trim(), 300);
  const [isExportModalOpened, exportModalOpenHandlers] = useBool();
  const { t } = useTranslation();
  const addressQueries = useAddressQueries();
  const { data: addressStats } = useQuery(addressQueries.stats());
  const {
    data: addresses,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
    isFetchingNextPage: isFetchingMoreAddresses,
    hasNextPage: hasMoreAddresses,
    fetchNextPage: loadMoreAddresses,
    error: addressesError,
  } = useInfiniteQuery(
    addressQueries.listInfinite({
      search: debouncedQuery,
      addressTypes: selectedAddressTypes,
    }),
  );
  const { data: addressTypes, error: addressTypesError } = useQuery(addressQueries.addressTypes());
  const deleteAddress = addressMutations.useDeleteAddress();
  const refreshLocationCode = addressMutations.useRegenerateLocationCode();
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingAddresses && !isFetchingMoreAddresses && hasMoreAddresses) {
        void loadMoreAddresses();
      }
    }, [isFetchingAddresses, isFetchingMoreAddresses, hasMoreAddresses, loadMoreAddresses]),
  });
  const mappedAddresses = useMemo(
    () =>
      addresses?.pages
        .flatMap((x) => x.items)
        .map((x) => ({
          ...x,
          address: formatAddress(x),
          registeredUsers: x.users.filter((u) => u.registeredAt),
          notRegisteredUsers: x.users.filter((u) => !u.registeredAt),
        })) || [],
    [addresses],
  );
  const hasPermission = usePermission();
  const canManage = hasPermission(canManageAddresses);
  const canEdit = hasPermission(canEditAddresses);

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<(typeof mappedAddresses)[number]>();

    return [
      helper.accessor("building.name", {
        header: t("page.addresses.list.building"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("address", {
        header: t("page.addresses.list.address"),
        cell: (cell) => cell.getValue(),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      ...(sessionUser.project.type === "addressBased"
        ? [
            helper.accessor("locationCode", {
              header: t("page.addresses.list.location-code"),
              cell: (cell) => (
                <Spread space="sm">
                  <span>{cell.getValue()}</span>
                  {canEdit ? (
                    <IconButton
                      variant="ghostPrimary"
                      title={t("page.addresses.list.refresh-location-code")}
                      onClick={() => setLocationCodeModalAddressId(cell.row.original.id)}
                      icon={<RefreshCcw01Icon />}
                      size="sm"
                    />
                  ) : null}
                </Spread>
              ),
              minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
              size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
              meta: {
                renderTruncatedTooltip: (cell) => cell.getValue(),
              },
            }),
          ]
        : []),
      ...(sessionUser.project.type === "addressBased"
        ? [
            helper.accessor("notRegisteredUsers", {
              header: t("page.addresses.list.invited-users"),
              cell: (cell) => <UsersList users={cell.getValue()} title={t("page.addresses.list.invited-users")} />,
              minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
              size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
            }),
            helper.accessor("registeredUsers", {
              header: t("page.addresses.list.onboarded-users"),
              cell: (cell) => <UsersList users={cell.getValue()} title={t("page.addresses.list.onboarded-users")} />,
              minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
              size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
            }),
          ]
        : []),
      helper.accessor("typeConstraint", {
        header: t("page.addresses.list.type"),
        cell: (cell) => cell.getValue()?.key ?? "-",
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) => {
            const value = cell.getValue();

            return value?.key ?? "-";
          },
        },
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              "data-testid": "context-menu-edit-btn",
              icon: <Edit05Icon />,
              type: "link",
              href: routes.addresses.edit({
                slug,
                id,
              }),
              isHidden: !canEdit,
            },
            {
              labels: {
                default: t("common.action.delete"),
                disabled: t("page.addresses.list.actions.delete.forbidden"),
              },
              value: "delete",
              "data-testid": "context-menu-delete-btn",
              icon: <Trash02Icon />,
              type: "button",
              callbackFn: () => openDeleteModal(cell.row.original),
              isHidden: !canManage,
              isDisabled: cell.row.original.users.some((x) => x.deletedAt == null),
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [canEdit, canManage, slug, openDeleteModal, sessionUser.project.type, t]);
  const tableInstance = useResizableTable(TABLE_KEYS.ADDRESSES, {
    columns,
    data: mappedAddresses,
    enableSorting: false,
  });

  const error = addressesError || addressTypesError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.addresses.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.addresses.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{
              scope: "project",
              entity: "addresses",
              page: "list",
              filters: {
                search: debouncedQuery,
                addressTypes: selectedAddressTypes,
              },
            }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.addresses.suggestion.1"),
              t("component.ask-nobi-chat-drawer.addresses.suggestion.2"),
            ]}
          />
          <Button icon={<Download01Icon />} variant="soft" onClick={exportModalOpenHandlers.setTrue}>
            {t("page.addresses.download-export")}
          </Button>
          {canManage && (
            <>
              <Button icon={<Upload01Icon />} variant="soft" type="link" href={routes.addresses.bulkUpload({ slug })}>
                {t("page.address.actions.bulk-upload")}
              </Button>
              <Button
                type="link"
                href={routes.addresses.new({
                  slug,
                })}
                data-testid="address-create-link"
              >
                {t("page.addresses.actions.create")}
              </Button>
            </>
          )}
        </>
      }
      header={
        <Inline gap="md" align="center" justify="between" collapseBelow="md">
          <Inline gap="sm" align="center" collapseBelow="md">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("page.addresses.list.search.placeholder")}
            />
            {addressTypes && addressTypes.length > 1 && (
              <div className="min-w-search-compact">
                <CheckboxMultiSelect<string>
                  renderMoreSelected={(count) => t("common.label.x-more", { count })}
                  placeholder={t("page.addresses.list.filter.placeholder")}
                  selected={selectedAddressTypes}
                  items={addressTypes}
                  renderOption={(x) => x}
                  keySelector={(x) => x}
                  onChange={setSelectedAddressTypes}
                />
              </div>
            )}
          </Inline>
          <p className="pl-space-sm text-right text-text-secondary">
            {addresses?.pages[0]?.total && (
              <span>
                {t("page.addresses.list.address-count", {
                  count: addresses?.pages[0]?.total,
                })}
              </span>
            )}
            {sessionUser.project.type === "addressBased" && addressStats && (
              <>
                <span> </span>
                <span>
                  {t("page.addresses.list.registered", {
                    registered: addressStats.registeredAddresses,
                    addresses: Math.max(0, addressStats.totalAddresses - 1),
                    percentage:
                      addressStats.totalAddresses - 1 <= 0
                        ? 0
                        : Math.min(
                            Math.round((addressStats.registeredAddresses / (addressStats.totalAddresses - 1)) * 100),
                            100,
                          ),
                  })}
                </span>
              </>
            )}
          </p>
        </Inline>
      }
    >
      {mappedAddresses.length === 0 && addressStats && !isLoadingAddresses && (
        <SectionCard>
          <p>
            {addressStats.totalAddresses === 0 ? t("page.addresses.list.empty") : t("page.addresses.list.no-result")}
          </p>
        </SectionCard>
      )}
      {(mappedAddresses.length > 0 || isFetchingAddresses) && (
        <Table
          table={tableInstance}
          getRowHref={(row) =>
            routes.addresses.edit({
              id: row.original.id,
              slug,
            })
          }
          data-testid="address-list"
          isLoading={isFetchingAddresses && !isFetchingMoreAddresses}
        />
      )}
      {hasMoreAddresses && <CollectionLoadingState ref={ref} />}
      <ExportAddressesModal
        isOpened={isExportModalOpened}
        onOpenChange={exportModalOpenHandlers.set}
        search={debouncedQuery}
        selectedAddressTypes={selectedAddressTypes}
      />
      <DeleteModal
        title={t("page.addresses.actions.delete.title")}
        description={t("page.addresses.actions.delete.description")}
        onDelete={deleteAddress.mutateAsync}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
      <ConfirmModal
        tone="warning"
        data-testid="location-code-modal"
        title={t("page.addresses.list.location-code.refresh.title")}
        description={t("page.addresses.list.location-code.refresh.description")}
        isOpened={!!locationCodeModalAddressId}
        onOpenChange={(state) => {
          if (!state) {
            setLocationCodeModalAddressId(undefined);
          }
        }}
        shouldCloseOnEsc
        onReject={() => setLocationCodeModalAddressId(undefined)}
        rejectBtnProps={{
          text: t("page.addresses.list.location-code.refresh.actions.cancel"),
        }}
        onResolve={async () => {
          try {
            if (locationCodeModalAddressId) {
              refreshingLocationCodeHandlers.setTrue();
              await refreshLocationCode.mutateAsync(locationCodeModalAddressId);
            }
          } finally {
            refreshingLocationCodeHandlers.setFalse();
            setLocationCodeModalAddressId(undefined);
          }
        }}
        resolveBtnProps={{
          text: t("page.addresses.list.location-code.refresh.actions.confirm"),
        }}
        isLoading={isRefreshingLocationCode}
      />
    </DocumentPage>
  );
}

function UsersList({ users, title }: { users: UserDto[]; title: string }) {
  const { t } = useTranslation();
  const [isModalOpened, setIsModalOpened] = useState(false);

  if (users.length === 0) {
    return <span>-</span>;
  }

  const firstUser = users[0];
  const remainingCount = users.length - 1;

  return (
    <>
      <Button size="sm" variant="ghostPrimary" onClick={() => setIsModalOpened(true)}>
        <span className="flex w-full items-center gap-0.5">
          <span className="truncate">{firstUser.fullName}</span>
          <span>{remainingCount > 0 ? `, ${t("common.label.x-more", { count: remainingCount })}` : ""}</span>
        </span>
      </Button>
      <AddressOverviewUserListModal
        isOpened={isModalOpened}
        onOpenChange={setIsModalOpened}
        title={title}
        users={users}
      />
    </>
  );
}
